import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "@/components/auth/AuthProvider";

const SESSION_KEY = "impersonation";

export interface ImpersonationState {
  isImpersonating: boolean;
  orgId: string | null;
  orgName: string | null;
  returnPath: string | null;
  authUid?: string;
}

export interface ImpersonationContextValue extends ImpersonationState {
  startImpersonation: (
    orgId: string,
    orgName: string,
    returnPath: string
  ) => void;
  stopImpersonation: () => void;
}

const defaultState: ImpersonationState = {
  isImpersonating: false,
  orgId: null,
  orgName: null,
  returnPath: null,
};

// eslint-disable-next-line react-refresh/only-export-components
export const ImpersonationContext =
  createContext<ImpersonationContextValue | null>(null);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const realAuth = useContext(AuthContext);
  const mayImpersonate = realAuth?.role === "PLATFORM_OWNER" || realAuth?.role === "PLATFORM_ADMIN";

  const [state, setState] = useState<ImpersonationState>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored && mayImpersonate) {
        const parsed = JSON.parse(stored);
        if (parsed.authUid === realAuth?.user?.uid && parsed.isImpersonating === true && typeof parsed.orgId === "string" && parsed.orgId) {
          return { isImpersonating: true, orgId: parsed.orgId, orgName: typeof parsed.orgName === "string" ? parsed.orgName : null,
            returnPath: typeof parsed.returnPath === "string" && parsed.returnPath.startsWith("/admin") ? parsed.returnPath : "/admin/orgs", authUid: parsed.authUid };
        }
      }
    } catch {
      /* ignore */
    }
    return defaultState;
  });

  // Sync to sessionStorage
  useEffect(() => {
    if (realAuth?.loading) return;
    if (state.isImpersonating && mayImpersonate && state.authUid === realAuth?.user?.uid) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [state, realAuth?.loading, realAuth?.user?.uid, mayImpersonate]);

  // Auto-exit when navigating to /admin/* (but not on initial activation,
  // since we're still on an /admin page when impersonation starts)
  const wasImpersonating = useRef(false);
  useEffect(() => {
    if (
      wasImpersonating.current &&
      state.isImpersonating &&
      location.pathname.startsWith("/admin")
    ) {
      setState(defaultState);
    }
    wasImpersonating.current = state.isImpersonating;
  }, [location.pathname, state.isImpersonating]);

  const startImpersonation = useCallback(
    (orgId: string, orgName: string, returnPath: string) => {
      if (!mayImpersonate) return;
      setState({ isImpersonating: true, orgId, orgName, returnPath, authUid: realAuth?.user?.uid });
    },
    [mayImpersonate, realAuth?.user?.uid]
  );

  const stopImpersonation = useCallback(() => {
    setState(defaultState);
  }, []);

  const value = useMemo(
    () => ({ ...(mayImpersonate && state.authUid === realAuth?.user?.uid ? state : defaultState), startImpersonation, stopImpersonation }),
    [state, mayImpersonate, realAuth?.user?.uid, startImpersonation, stopImpersonation]
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}
